// ============================================================
// A real, tiny PDF, built in the browser.
//
// The sample portfolio's documents are fixtures: they have a name, a type and
// a date, but no file behind them. So View and Download were dead — the row
// greys itself out when `fileUrl` is missing, and on a demo that reads as a
// broken feature rather than an absent file.
//
// This writes a genuine one-page PDF so both actions do what they say: View
// opens it in the app's own pdf.js viewer (the same component a real document
// uses), and Download saves a file that opens anywhere. Every page is clearly
// watermarked as a sample — a demo must never hand someone a document that
// could be mistaken for an executed one.
//
// Hand-rolled rather than a library: this is ~2KB of string building against
// a 1994 file format, versus ~300KB of pdf-lib in the bundle for one screen.
// ============================================================

/** Escape the three characters that terminate a PDF string. */
const esc = (s: string) => String(s).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');

/** One line of text at an absolute position, in points from the bottom-left. */
const line = (x: number, y: number, size: number, font: string, text: string) =>
  `BT /${font} ${size} Tf ${x} ${y} Td (${esc(text)}) Tj ET\n`;

/**
 * A single-page A4 document. `rows` are label/value pairs printed as a simple
 * two-column list — enough to look like the record it stands in for.
 */
export function buildSamplePdf(title: string, subtitle: string, rows: { label: string; value: string }[]): Blob {
  let content = '';

  // Gold rule under the wordmark (RGB 0.84 0.69 0.38 = #D6B160).
  content += '0.84 0.69 0.38 rg 60 770 90 3 re f\n';
  content += '0 0 0 rg\n';
  content += line(60, 800, 11, 'F2', 'INVESTMENTER');
  content += line(60, 735, 20, 'F2', title);
  if (subtitle) content += line(60, 712, 11, 'F1', subtitle);

  let y = 665;
  for (const r of rows.slice(0, 18)) {
    content += '0.45 0.45 0.45 rg\n' + line(60, y, 10, 'F1', r.label);
    content += '0 0 0 rg\n' + line(250, y, 11, 'F2', r.value);
    // hairline
    content += '0.85 0.85 0.85 rg 60 ' + (y - 9) + ' 475 0.6 re f\n0 0 0 rg\n';
    y -= 30;
  }

  // Watermark, last so it sits over the content.
  content += 'q 0.90 0.84 0.72 rg BT /F2 46 Tf 1 0 0 1 150 380 Tm 30 Tz (SAMPLE DOCUMENT) Tj ET Q\n';
  content += '0.45 0.45 0.45 rg\n' + line(60, 60, 9, 'F1', 'Sample document generated by the Asset Desk demo. Not a contract.');

  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents 4 0 R '
      + '/Resources << /Font << /F1 5 0 R /F2 6 0 R >> >> >>',
    `<< /Length ${content.length} >>\nstream\n${content}endstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>',
  ];

  // Assemble, recording each object's byte offset for the xref table — pdf.js
  // can recover from a broken one, but a correct file is a correct file.
  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((body, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${body}\nendobj\n`;
  });

  const xrefAt = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((o) => { pdf += String(o).padStart(10, '0') + ' 00000 n \n'; });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF`;

  return new Blob([pdf], { type: 'application/pdf' });
}
